import React, { useEffect, useRef } from 'react';

export const NetworkGraph: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Resize canvas to match container using ResizeObserver
    // so it always fills the space correctly
    const resize = () => {
      canvas.width  = container.clientWidth;
      canvas.height = container.clientHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    // Generate nodes
    const NUM_NODES = 120;
    const COLORS = ['#6366f1', '#8b5cf6', '#22d3ee', '#10b981', '#f59e0b', '#f43f5e', '#a78bfa'];

    const nodes = Array.from({ length: NUM_NODES }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 1.5,
      vy: (Math.random() - 0.5) * 1.5,
      r:  Math.random() > 0.88 ? 6 : Math.random() > 0.7 ? 4 : 2.5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

    // Edges — only between close nodes
    const edges: { s: number; t: number }[] = [];
    for (let i = 0; i < NUM_NODES; i++) {
      const n = Math.floor(Math.random() * 3) + 1;
      for (let j = 0; j < n; j++) {
        const t = Math.floor(Math.random() * NUM_NODES);
        if (t !== i) edges.push({ s: i, t });
      }
    }

    let raf: number;

    const render = () => {
      const w = canvas.width;
      const h = canvas.height;

      // Fade trail
      ctx.fillStyle = 'rgba(15,23,42,0.25)';
      ctx.fillRect(0, 0, w, h);

      // Move nodes
      for (const node of nodes) {
        node.x += node.vx * 0.25;
        node.y += node.vy * 0.25;
        if (node.x < 0 || node.x > w) node.vx *= -1;
        if (node.y < 0 || node.y > h) node.vy *= -1;
        node.x = Math.max(0, Math.min(w, node.x));
        node.y = Math.max(0, Math.min(h, node.y));
      }

      // Draw edges
      ctx.lineWidth = 0.5;
      ctx.shadowBlur = 0;
      for (const { s, t } of edges) {
        const a = nodes[s], b = nodes[t];
        const dx = b.x - a.x, dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 160) {
          const alpha = (1 - dist / 160) * 0.35;
          ctx.strokeStyle = `rgba(148,163,184,${alpha})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // Draw nodes
      for (const node of nodes) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        if (node.r > 4) {
          ctx.shadowBlur = 12;
          ctx.shadowColor = node.color;
        } else {
          ctx.shadowBlur = 0;
        }
        ctx.fill();
      }

      raf = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%', background: '#0f172a' }}
      />
    </div>
  );
};
